import TrapFocus from '../utils/trap-focus';
import './cart-incentives';
import variantColor from '../utils/colors';

export default class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.trapFocus = null;
    this.upsellSlider = null;
    this.isCartRedirect = new URLSearchParams(window.location.search).get(
      'cart',
    );
  }

  bindEvents() {
    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close(),
    );
    this.querySelector('[data-overlay]').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.querySelector('.drawer__close').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.headerToggleDrawer();
    this.initUpsells();
    this.updateSubTotal();
    this.defaultOpen();
    this.setupCheckoutEvent();
    variantColor();
    document.addEventListener('shopify:section:load', () => {
      if (this.upsellSlider) {
        this.upsellSlider.update();
      }
    });
  }

  connectedCallback() {
    this.bindEvents();
  }

  setupCheckoutEvent() {
    document
      .querySelector('#cart-drawer-form')
      .addEventListener('submit', (e) => {
        e.preventDefault();

        if (window.gtag) {
          window.gtag('js', new Date());
          window.gtag('event', 'beginCheckout', {
            id: '11054038411',
            label: 'jYxpCNqwnYkYEIv7_JYp',
          });
        }

        window.location.href = '/checkout';
      });
  }

  headerToggleDrawer() {
    const cartLink = document.querySelector('[data-header-cart-trigger]');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  defaultOpen() {
    if (this.isCartRedirect) {
      this.open();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    document.body.classList.add('cart-open');
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('[data-cart-empty]')
      : document.getElementById('CartDrawer');
    const focusElement =
      this.querySelector('.drawer__inner') ||
      this.querySelector('.drawer__close');
    if (!this.trapFocus) {
      this.trapFocus = new TrapFocus(containerToTrapFocusOn, focusElement);
    }
  }

  close() {
    if (this.activeElement) {
      this.trapFocus.removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('cart-open');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateCartCount(cartQuantity) {
    const cartCountEl = document.querySelector('[data-cart-count]');
    cartCountEl.setAttribute('data-cart-count', cartQuantity);
    cartCountEl.textContent = cartQuantity;
    if (parseInt(cartQuantity, 10) <= 0) {
      cartCountEl.classList.add('hidden');
    } else {
      cartCountEl.classList.remove('hidden');
    }
  }

  renderContents(parsedHTML) {
    const cartData = parsedHTML ? JSON.parse(parsedHTML.textContent) : {};
    const drawerInner = document.querySelector('.drawer__inner');

    if (parseInt(cartData.item_count, 10) > 0) {
      drawerInner.classList.remove('flex', 'flex-col', 'flex-1');
    } else {
      drawerInner.classList.add('flex', 'flex-col', 'flex-1');
    }

    this.updateCartCount(cartData.item_count ?? 0);

    this.querySelector('[data-overlay]').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.querySelector('.drawer__close').addEventListener(
      'click',
      this.close.bind(this),
    );

    variantColor();
    this.initUpsells();
    this.setupGWP(cartData);
  }

  initUpsells() {
    this.upsellSlider = this.querySelector('.cart-upsells');
    const swiperOptions = {
      slidesPerView: 'auto',
      centeredSlides: false,
      spaceBetween: 6,
      slidesOffsetAfter: 90,
      navigation: false,
    };
    if (this.upsellSlider) {
      Object.assign(this.upsellSlider, swiperOptions);
      this.upsellSlider.initialize();
    }
  }

  setupGWP(cartData) {
    const dataEl = this.querySelector('[data-incentives]');
    this.thresholdData = dataEl ? JSON.parse(dataEl.textContent) : {};
    if (!this.thresholdData) return;

    this.gwpFilteredData = this.thresholdData.filter(
      (data) => data.type === 'gwp' && data.product?.id,
    );

    if (!this.gwpFilteredData.length) return;

    this.manageGWP(cartData?.items);
  }

  manageGWP(lines) {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    const gwpLineItems = lines.filter((line) => line.properties?._gwp);
    const cartSubtotal = this.calculateCartTotalWithGWP(
      lines.filter((line) => !line.properties?._gwp),
    );

    const productsToAdd = [];
    const productsToRemove = [];
    if (this.gwpFilteredData.length > 0) {
      this.gwpFilteredData.forEach((data) => {
        if (!data.product?.id) return;
        const gwpLineItem = gwpLineItems.find(
          (line) => line.product_id === data.product?.id,
        );
        if (cartSubtotal >= data.threshold) {
          const selectedVariant = data.product.variants[0];
          const attributes = [
            { key: '_gwp', value: `${data.product.id}` },
            {
              key: '_gwp_guid',
              value: `${data.guid}`,
            },
          ];
          if (!gwpLineItem) {
            productsToAdd.push({
              merchandiseId: `${selectedVariant.id}`,
              attributes,
            });
          }
        } else if (cartSubtotal < data.threshold) {
          if (gwpLineItem) {
            productsToRemove.push(gwpLineItem.key);
          }
        }
      });

      if (productsToAdd.length) {
        const addToCartEvent = new CustomEvent('cart:add', {
          detail: {
            payload: productsToAdd,
          },
        });
        document.dispatchEvent(addToCartEvent);
      }

      if (productsToRemove.length) {
        cartDrawerItems.removeLineItem(productsToRemove);
      }
    }
  }

  calculateCartTotalWithGWP(cartItems) {
    let cartTotal = 0;

    cartItems.forEach((item) => {
      if (item.properties?._gwp) return;
      cartTotal += item.final_line_price;
    });
    const finalTotal = cartTotal > 0 ? cartTotal / 100 : 0;
    this.updateSubTotal(finalTotal);
    return finalTotal;
  }

  updateSubTotal(total) {
    if (total || total === 0) {
      document.querySelector('[data-cart-subtotal]').textContent = `$${total}`;
    } else {
      const originalCartData = document.querySelector('[data-cart-data]');
      const parsedCartData = originalCartData
        ? JSON.parse(originalCartData.textContent)
        : null;

      if (!parsedCartData) return;
      this.calculateCartTotalWithGWP(parsedCartData.items);
    }
  }

  variantColor() {
    document
      .querySelectorAll('[data-variant-color]')
      .forEach((variantElement) => {
        const currentColor = variantElement.getAttribute('data-current-color');

        const color = window.colors.find((item) => item.name === currentColor);

        if (color) {
          const hexElement = variantElement.querySelector('[data-variant-hex]');
          const nameElement = variantElement.querySelector(
            '[data-variant-name]',
          );

          if (hexElement) hexElement.style.backgroundColor = color.color;
          if (nameElement) nameElement.textContent = color.name;
        }
      });
  }
}

export const webcomponent = 'cart-drawer';
